import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import socket from '../utils/socket';
import { usePermissions } from '../context/PermissionsContext';
import OrdersBoard from '../components/OrdersBoard';
import OrderAnalytics from '../components/OrderAnalytics';
import { SkelTable } from '../components/Skeletons';
import { T } from '../theme/casa';
import '../ui-pages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Replace an item if present (by key), else prepend it.
const upsertBy = (list, item, key) => {
  if (!item || item[key] == null) return list;
  const idx = list.findIndex((x) => String(x[key]) === String(item[key]));
  if (idx === -1) return [item, ...list];
  const next = [...list];
  next[idx] = { ...next[idx], ...item };
  return next;
};

// Status tones mapped to the Casa Libre state pairs (meaning preserved).
const fulfillmentTone = (s) => {
  if (s === 'fulfilled') return { label: 'Fulfilled', color: T.success, bg: T.successSurface };
  if (s === 'partial') return { label: 'Partial', color: T.info, bg: T.infoSurface };
  if (s === 'restocked') return { label: 'Restocked', color: T.textMuted, bg: T.bgSurface };
  return { label: 'Unfulfilled', color: T.warning, bg: T.warningSurface };
};
const financialTone = (s) => {
  if (s === 'paid') return { color: T.success, bg: T.successSurface };
  if (s === 'pending') return { color: T.warning, bg: T.warningSurface };
  if (s === 'refunded' || s === 'voided') return { color: T.danger, bg: T.dangerSurface };
  return { color: T.textMuted, bg: T.bgSurface };
};
// Carrier delivery status (Shopify fulfillment.shipment_status).
const deliveryTone = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'delivered') return { label: 'Delivered', color: T.success, bg: T.successSurface };
  if (v === 'out_for_delivery') return { label: 'Out for delivery', color: T.info, bg: T.infoSurface };
  if (v === 'in_transit') return { label: 'In transit', color: T.info, bg: T.infoSurface };
  if (v === 'attempted_delivery') return { label: 'Attempted', color: T.warning, bg: T.warningSurface };
  if (v === 'failure') return { label: 'Failed', color: T.danger, bg: T.dangerSurface };
  if (['confirmed', 'carrier_picked_up', 'ready_for_pickup'].includes(v)) return { label: 'Picked up', color: T.info, bg: T.infoSurface };
  if (['label_printed', 'label_purchased'].includes(v)) return { label: 'Label created', color: T.textMuted, bg: T.bgSurface };
  return null;
};

const Badge = ({ color, bg, children, cap }) => (
  <span className="ord-badge" style={{ color, background: bg, textTransform: cap ? 'capitalize' : 'none' }}>
    <span className="ord-badge-dot" style={{ background: color }} />
    {children}
  </span>
);

// Pagination bar shown at the top of a table.
const Pagination = ({ page, total, pageSize, onGo }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  const nums = [];
  const start = Math.max(0, page - 2);
  const end = Math.min(pages - 1, page + 2);
  if (start > 0) { nums.push(0); if (start > 1) nums.push('…'); }
  for (let i = start; i <= end; i++) nums.push(i);
  if (end < pages - 1) { if (end < pages - 2) nums.push('…'); nums.push(pages - 1); }
  return (
    <div className="ord-pager">
      <span className="ord-pager-info">{from}–{to} of {total}</span>
      <div className="ord-pager-btns">
        <button className="ord-pg" disabled={page === 0} onClick={() => onGo(page - 1)}>‹ Prev</button>
        {nums.map((n, i) => n === '…'
          ? <span key={`e${i}`} className="ord-pg-dots">…</span>
          : <button key={n} className={`ord-pg ${n === page ? 'on' : ''}`} onClick={() => onGo(n)}>{n + 1}</button>)}
        <button className="ord-pg" disabled={page >= pages - 1} onClick={() => onGo(page + 1)}>Next ›</button>
      </div>
    </div>
  );
};

const Orders = () => {
  const { workspaceUuid } = useParams();
  const { can, readOnly } = usePermissions();
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState('');
  const [stores, setStores] = useState([]);          // connected Shopify stores
  const [activeStore, setActiveStore] = useState(null); // selected shopDomain
  const PAGE_SIZE = 50;
  const [ordPage, setOrdPage] = useState(0);
  const [chkPage, setChkPage] = useState(0);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [checkoutsTotal, setCheckoutsTotal] = useState(0);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });

  // Scope a request to one store (only when there are 2+).
  // Single combined view — all brands/stores in one tab (no per-store scoping).
  const storeQS = () => '';
  const storeBody = () => ({});

  const loadOrders = async (page = 0) => {
    setErr('');
    try {
      const r = await axios.get(`${API_URL}/api/integrations/shopify/orders?limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}${storeQS()}`, auth());
      setOrders(r.data.items || []);
      setOrdersTotal(r.data.total || 0);
    } catch (e) {
      setErr(e.response?.data?.message ? String(e.response.data.message) : 'Could not load orders. Is Shopify connected?');
    }
  };

  const loadCheckouts = async (page = 0) => {
    setErr('');
    try {
      const r = await axios.get(`${API_URL}/api/integrations/shopify/checkouts?limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}${storeQS()}`, auth());
      setCheckouts(r.data.items || []);
      setCheckoutsTotal(r.data.total || 0);
    } catch (e) {
      setErr(e.response?.data?.message ? String(e.response.data.message) : 'Could not load abandoned checkouts.');
    }
  };

  // Jump to a page (clamped) and load it.
  const goOrdPage = (p) => { const np = Math.max(0, Math.min(p, Math.ceil(ordersTotal / PAGE_SIZE) - 1)); setOrdPage(np); loadOrders(np); };
  const goChkPage = (p) => { const np = Math.max(0, Math.min(p, Math.ceil(checkoutsTotal / PAGE_SIZE) - 1)); setChkPage(np); loadCheckouts(np); };

  // Load the connected stores for the toggle.
  useEffect(() => {
    if (!workspaceUuid) return;
    axios.get(`${API_URL}/api/workspaces/${workspaceUuid}/integrations`).then(r => {
      const connected = (r.data?.shopify || []).filter(s => s.connected);
      setStores(connected);
      setActiveStore(prev => (connected.find(s => s.shopDomain === prev) ? prev : (connected[0]?.shopDomain || null)));
    }).catch(() => setStores([]));
  }, [workspaceUuid]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setOrdPage(0); setChkPage(0);
      await Promise.all([loadOrders(0), loadCheckouts(0)]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid, activeStore]);

  // Live updates from Shopify webhooks.
  useEffect(() => {
    if (!workspaceUuid) return;
    socket.emit('joinWorkspace', workspaceUuid);
    const onOrder = (o) => setOrders((prev) => upsertBy(prev, o, 'shopifyOrderId'));
    const onCheckout = (c) => setCheckouts((prev) => upsertBy(prev, c, 'shopifyCheckoutId'));
    socket.on('shopify:order:created', onOrder);
    socket.on('shopify:order:updated', onOrder);
    socket.on('shopify:checkout:created', onCheckout);
    return () => {
      socket.off('shopify:order:created', onOrder);
      socket.off('shopify:order:updated', onOrder);
      socket.off('shopify:checkout:created', onCheckout);
    };
  }, [workspaceUuid]);

  const refresh = async () => {
    setSyncing(true);
    try {
      if (tab === 'orders') {
        await axios.post(`${API_URL}/api/integrations/shopify/orders/sync`, storeBody(), auth());
        setOrdPage(0); await loadOrders(0);
      } else {
        await axios.post(`${API_URL}/api/integrations/shopify/checkouts/sync`, storeBody(), auth());
        setChkPage(0); await loadCheckouts(0);
      }
    } catch (e) {
      setErr(e.response?.data?.message ? String(e.response.data.message) : 'Sync failed.');
    }
    setSyncing(false);
  };

  const money = (v, c) => v != null ? `${c || ''} ${v}`.trim() : '—';
  const when = (d) => d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const initials = (s = '') => (s.trim()[0] || '?').toUpperCase();

  // The Order Tracking board (wip_orders) is merged INTO the main Orders tab.
  // Tab order: Orders → Analytics → Abandoned Checkouts. (Production WIP / Live
  // Stock are intentionally not shown on this page.)
  const canBoard = can('wip_orders');
  const tabs = [
    ...((can('orders') || canBoard) ? [{ k: 'orders', label: 'Orders', n: canBoard ? undefined : ordersTotal }] : []),
    ...(canBoard ? [{ k: 'analytics', label: 'Analytics' }] : []),
    ...(can('orders') ? [{ k: 'abandoned', label: 'Abandoned Checkouts', n: checkoutsTotal }] : []),
  ];
  const brandName = (stores.find(s => s.shopDomain === activeStore) || stores[0])?.shopName
    || activeStore?.replace('.myshopify.com', '') || 'Store';

  // Keep the active tab on something the user can actually see.
  useEffect(() => {
    const allowed = tabs.map(t => t.k);
    setTab(prev => allowed.includes(prev) ? prev : (allowed[0] || 'orders'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can]);

  // Read-only (test/demo) accounts have no access to Orders at all — show a
  // clear block screen instead of the board (backend blocks the data too).
  if (readOnly) {
    return (
      <div className="ui-page">
        <style>{css}</style>
        <header className="ui-head"><div><h1 className="ui-title">Orders</h1></div></header>
        <div className="ord-block">
          <div className="ord-block-ic">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          </div>
          <h2>Orders are not available on this account</h2>
          <p>This is a <b>demo / test account</b>. The Orders workspace is restricted — you have read-only access to the rest of the app.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ui-page">
      <style>{css}</style>
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Orders</h1>
        </div>
        <div className="ui-head-actions" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div className="ord-tabs" style={{ marginBottom: 0 }}>
            {tabs.map(t => (
              <button key={t.k} className={`ord-tab ${tab === t.k ? 'on' : ''}`} onClick={() => setTab(t.k)}>
                {t.label}{t.n != null && <span className="ord-tab-n">{t.n}</span>}
              </button>
            ))}
          </div>
          {((tab === 'orders' && !canBoard) || tab === 'abandoned') && (
            <button className="ord-refresh" onClick={refresh} disabled={syncing}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className={syncing ? 'ord-spin' : ''}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
              {syncing ? 'Syncing…' : 'Refresh'}
            </button>
          )}
        </div>
      </header>

      {err && <div className="ord-err">{err}</div>}

      {tab === 'analytics' ? (
        <OrderAnalytics workspaceUuid={workspaceUuid} shopDomain={undefined} brandName={brandName} />
      ) : tab === 'orders' && canBoard ? (
        <OrdersBoard workspaceUuid={workspaceUuid} shopDomain={undefined} brandName={brandName} />
      ) : loading ? (
        <SkelTable rows={10} cols={7} />
      ) : tab === 'orders' ? (
        orders.length === 0 ? (
          <div className="ui-empty">
            <div className="ui-empty-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg></div>
            <h2>No orders yet</h2>
            <p>New orders will appear here automatically via webhook (and on refresh).</p>
          </div>
        ) : (
          <div className="ord-table-card">
            <Pagination page={ordPage} total={ordersTotal} pageSize={PAGE_SIZE} onGo={goOrdPage} />
            <div className="ord-scroll">
              <table className="ord-table">
                <thead>
                  <tr>
                    <th>Order</th><th>Date</th><th>Customer</th><th>Contact</th>
                    <th className="ord-right">Total</th><th>Payment</th><th>Fulfillment</th><th>Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const ft = fulfillmentTone(o.fulfillmentStatus);
                    const fin = financialTone(o.financialStatus);
                    return (
                      <tr key={o.shopifyOrderId}>
                        <td>
                          <span className="ord-name">{o.name || `#${o.orderNumber}`}</span>
                          {(o.tags || []).length > 0 && (
                            <div className="ord-tags">{o.tags.map((t, ti) => <span key={ti} className="ord-chip">{t}</span>)}</div>
                          )}
                        </td>
                        <td className="ord-muted">{when(o.createdAt)}</td>
                        <td>
                          <div className="ord-cust">
                            <span className="ord-av">{initials(o.customerName || o.email || '?')}</span>
                            <span className="ord-cust-name">{o.customerName || '—'}</span>
                          </div>
                        </td>
                        <td className="ord-contact">
                          {o.phone && <div>{o.phone}</div>}
                          {o.email && <div className="ord-muted">{o.email}</div>}
                          {!o.phone && !o.email && <span className="ord-muted">—</span>}
                        </td>
                        <td className="ord-right ord-total">{money(o.totalPrice, o.currency)}</td>
                        <td>{o.financialStatus ? <Badge color={fin.color} bg={fin.bg} cap>{o.financialStatus}</Badge> : <span className="ord-muted">—</span>}</td>
                        <td><Badge color={ft.color} bg={ft.bg}>{ft.label}</Badge></td>
                        <td>
                          {(() => {
                            const dt = deliveryTone(o.deliveryStatus);
                            if (!dt && !o.trackingNumber) return <span className="ord-muted">—</span>;
                            return (
                              <>
                                {dt && <Badge color={dt.color} bg={dt.bg}>{dt.label}</Badge>}
                                {o.trackingNumber && (
                                  <div className="ord-track">
                                    {o.trackingCompany ? `${o.trackingCompany} · ` : ''}
                                    {o.trackingUrl
                                      ? <a href={o.trackingUrl} target="_blank" rel="noreferrer">{o.trackingNumber}</a>
                                      : o.trackingNumber}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        checkouts.length === 0 ? (
          <div className="ui-empty">
            <div className="ui-empty-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg></div>
            <h2>No abandoned checkouts</h2>
            <p>We poll Shopify every 2 minutes for the latest abandoned checkouts — new ones show up here automatically.</p>
          </div>
        ) : (
          <div className="ord-table-card">
            <Pagination page={chkPage} total={checkoutsTotal} pageSize={PAGE_SIZE} onGo={goChkPage} />
            <div className="ord-scroll">
              <table className="ord-table">
                <thead>
                  <tr>
                    <th>Customer</th><th>Date</th><th>Contact</th>
                    <th className="ord-right">Items</th><th className="ord-right">Total</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {checkouts.map((c) => (
                    <tr key={c.shopifyCheckoutId}>
                      <td>
                        <div className="ord-cust">
                          <span className="ord-av">{initials(c.customerName || c.email || c.phone || 'G')}</span>
                          <span className="ord-cust-name">{c.customerName || c.email || c.phone || 'Guest'}</span>
                        </div>
                      </td>
                      <td className="ord-muted">{when(c.createdAt)}</td>
                      <td className="ord-contact">
                        {c.phone && <div>{c.phone}</div>}
                        {c.email && <div className="ord-muted">{c.email}</div>}
                        {!c.phone && !c.email && <span className="ord-muted">—</span>}
                      </td>
                      <td className="ord-right">{(c.lineItems || []).length}</td>
                      <td className="ord-right ord-total">{money(c.totalPrice, c.currency)}</td>
                      <td><Badge color={T.warning} bg={T.warningSurface}>Abandoned</Badge></td>
                      <td className="ord-right">
                        {c.abandonedCheckoutUrl && (
                          <a href={c.abandonedCheckoutUrl} target="_blank" rel="noreferrer" className="ord-recover">
                            Recovery
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
};

const css = `
.ord-refresh{ display:inline-flex; align-items:center; gap:8px; background:#111111; color:#F9F4EE; border:none; padding:9px 16px; border-radius:999px; font-weight:700; font-size:13px; cursor:pointer; box-shadow:4px 4px 0 rgba(17,17,17,.2); transition:background .15s, transform .1s; }
.ord-refresh:hover{ background:#000000; }
.ord-refresh:active{ transform:translateY(1px); }
.ord-refresh:disabled{ opacity:.5; cursor:default; }
.ord-spin{ animation:ordSpin .8s linear infinite; }
@keyframes ordSpin{ to{ transform:rotate(360deg); } }

.ord-tabs{ display:flex; gap:8px; margin-bottom:18px; flex-wrap:wrap; align-items:center; }
.ord-tab{ background:#fff; border:1px solid #E7E1D6; cursor:pointer; font-size:13px; font-weight:500; color:#3A3A37; padding:8px 15px; border-radius:999px; display:flex; align-items:center; gap:8px; white-space:nowrap; transition:color .15s, background .15s, border-color .15s; }
.ord-tab:hover{ color:#111111; background:#FAF7F1; }
.ord-tab.on{ background:#111111; color:#F9F4EE; border-color:#111111; }
.ord-tab-n{ font-size:11px; font-weight:500; background:#FAF7F1; color:#9C978C; border-radius:999px; padding:1px 8px; font-family:'IBM Plex Mono',monospace; }
.ord-tab.on .ord-tab-n{ background:rgba(249,244,238,.2); color:#F9F4EE; }

.ord-err{ font-size:13px; color:#8A2B16; background:#FBEDE9; border:1px solid #E7E1D6; padding:11px 14px; border-radius:14px; margin-bottom:16px; font-weight:600; }

.ord-table-card{ background:#FFFFFF; border:1px solid #E7E1D6; border-radius:14px; overflow:hidden; }
.ord-scroll{ overflow-x:auto; }
.ord-table{ width:100%; border-collapse:collapse; min-width:760px; }
.ord-table thead th{ text-align:left; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; padding:8px 14px; background:#FAF7F1; border-bottom:1px solid #E7E1D6; white-space:nowrap; }
.ord-table thead th.ord-right{ text-align:right; }
.ord-table tbody td{ padding:9px 14px; border-bottom:1px solid #E7E1D6; font-size:12.5px; color:#3A3A37; vertical-align:middle; }
.ord-table tbody tr:last-child td{ border-bottom:none; }
.ord-table tbody tr{ transition:background .12s; }
.ord-table tbody tr:hover{ background:#FAF7F1; }
.ord-right{ text-align:right; }
.ord-muted{ color:#9C978C; }
.ord-name{ font-weight:700; color:#111111; font-size:14px; font-family:'IBM Plex Mono',monospace; }
.ord-total{ font-weight:700; color:#111111; white-space:nowrap; font-family:'IBM Plex Mono',monospace; }
.ord-contact{ font-size:12.5px; line-height:1.5; }

.ord-cust{ display:flex; align-items:center; gap:9px; }
.ord-av{ width:28px; height:28px; border-radius:50%; flex:none; background:#111111; color:#F9F4EE; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; }
.ord-cust-name{ font-weight:700; color:#111111; white-space:nowrap; }

.ord-badge{ display:inline-flex; align-items:center; gap:6px; padding:3px 10px; border-radius:999px; font-size:11.5px; font-weight:700; white-space:nowrap; }
.ord-badge-dot{ width:6px; height:6px; border-radius:50%; flex:none; }

.ord-pager{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:11px 16px; border-bottom:1px solid #E7E1D6; background:#FAF7F1; flex-wrap:wrap; }
.ord-pager-info{ font-size:12.5px; font-weight:700; color:#6B6862; }
.ord-pager-btns{ display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
.ord-pg{ background:#FFFFFF; border:1px solid #E7E1D6; color:#3A3A37; font-size:12.5px; font-weight:700; padding:6px 11px; border-radius:999px; cursor:pointer; min-width:34px; transition:background .12s, color .12s, border-color .12s; }
.ord-pg:hover:not(:disabled){ background:#FAF7F1; border-color:#E7E1D6; color:#111111; }
.ord-pg.on{ background:#111111; border-color:#111111; color:#F9F4EE; }
.ord-pg:disabled{ opacity:.45; cursor:default; }
.ord-pg-dots{ color:#9C978C; font-weight:800; padding:0 2px; }
.ord-track{ font-size:11px; color:#6B6862; margin-top:5px; font-family:'IBM Plex Mono',monospace; }
.ord-track a{ color:#111111; text-decoration:none; }
.ord-track a:hover{ text-decoration:underline; }
.ord-tags{ display:flex; gap:5px; flex-wrap:wrap; margin-top:6px; }
.ord-chip{ font-size:10.5px; font-weight:700; color:#3A3A37; background:#FAF7F1; border:1px solid #E7E1D6; padding:2px 8px; border-radius:999px; white-space:nowrap; }

.ord-recover{ display:inline-flex; align-items:center; gap:6px; background:#FFFFFF; color:#3A3A37; border:1px solid #E7E1D6; padding:6px 11px; border-radius:999px; font-weight:700; font-size:12px; text-decoration:none; white-space:nowrap; transition:background .15s, color .15s, border-color .15s; }
.ord-recover:hover{ background:#111111; color:#F9F4EE; border-color:#111111; }

.ord-block{ background:#FFFFFF; border:1px solid #E7E1D6; border-radius:14px; padding:52px 28px; text-align:center; max-width:520px; margin:24px auto; }
.ord-block-ic{ width:64px; height:64px; border-radius:50%; margin:0 auto 18px; background:#F6E4E1; color:#B23A3A; display:flex; align-items:center; justify-content:center; }
.ord-block h2{ font-size:19px; font-weight:700; letter-spacing:-0.03em; color:#111111; margin:0 0 8px; }
.ord-block p{ font-size:14px; color:#6B6862; margin:0 auto; max-width:400px; line-height:1.6; }
`;

export default Orders;
